// Local practice only; these checks never submit final-quiz attempts.

export const PRACTICE_VERSION = 'lesson-checks-v3';

const choice = (id, prompt, labels, correct, explanation) => ({

  id,

  type: 'choice',

  prompt,

  choices: labels.map((label, index) => ({ id: String(index), label })),

  correct: String(correct),

  explanation,

});

const TASK_COPY = {

  'arrival-message': [

    'Let the group know you’ve arrived.',

    'You’re hosting Museum & Wine Bar and are waiting by the museum entrance. Open the event portal and send a message explaining where guests can find you.',

  ],

  'guest-attendance': [

    'Update guest attendance.',

    'Alex has arrived—check Alex in. Later, the event ends without Jordan arriving. Mark Jordan as a no-show.',

  ],

  'send-request': [

    'Send Alex a friend request.',

    'You met Alex at Museum & Wine Bar and would like to stay connected. Find Alex and send a friend request.',

  ],

  'accept-request': [

    'Accept Jordan’s friend request.',

    'Jordan sent you a friend request after the event. Find the request and accept it.',

  ],

  'contact-message': [

    'Send Alex a message.',

    'Alex is already in your contacts. Open Alex’s chat and send a follow-up message.',

  ],

};

const task = (id, taskKind, scenario, explanation) => ({

  id,

  type: 'task',

  taskKind,

  prompt: TASK_COPY[taskKind][0],

  help: TASK_COPY[taskKind][1],

  scenario,

  explanation,

});

const check = (afterLesson, section, title, questions) => ({

  id: afterLesson,

  afterLesson,

  section,

  title,

  questions,

});

export const SECTION_CHECKS = [

  check('what-is-tribe', 'Background', 'What Is Tribe?', [

    choice(

      'what-tribe-does',

      'What does Tribe do?',

      [

        'Helps people make friends through curated, host-led experiences.',

        'Helps people find friends through profiles and online matching.',

        'Helps members organize their own meetups through a shared calendar.',

        'Helps people find discounted tickets to local activities and venues.',

      ],

      0,

      'Tribe is free and invite-only. It brings people together through curated, host-led experiences and gives them a platform to stay connected afterward.',

    ),

  ]),

  check('event-structure', 'Background', 'Event Structure', [

    {

      id: 'seven-stages',

      type: 'order',

      prompt: 'Put the seven event stages in order.',

      help: 'Arrange the stages from the guests’ arrival to the event’s closing. Drag the cards, use the arrow buttons, or use the arrow keys on a drag handle.',

      items: [

        { id: 'welcome', label: 'Welcome' },

        { id: 'introduction', label: 'Introduction' },

        { id: 'icebreaker', label: 'Icebreaker' },

        { id: 'first-activity', label: 'First Activity' },

        { id: 'transition', label: 'Transition' },

        { id: 'second-activity', label: 'Second Activity' },

        { id: 'closing', label: 'Closing' },

      ],

      initialOrder: ['first-activity', 'welcome', 'second-activity', 'introduction', 'closing', 'icebreaker', 'transition'],

      explanation: 'Welcome → Introduction → Icebreaker → First Activity → Transition → Second Activity → Closing. This flow helps guests settle in, connect through the activities, and leave with a clear way to stay in touch.',

    },

  ]),

  check('your-role', 'Background', 'Your Role as a Host', [

    choice(

      'host-role',

      'What are your two main responsibilities as a host?',

      [

        'Choose the venues and plan the event’s activities.',

        'Manage the event flow and help guests connect.',

        'Welcome the guests and let the group run the event.',

        'Follow the agenda and leave conversation to the guests.',

      ],

      1,

      'Tribe designs the event. Your job is to guide the group through it and actively help guests feel comfortable, participate, and connect with one another.',

    ),

  ]),

  check('event-offer', 'Before an Event', 'Event Offer', [

    choice(

      'receive-event',

      'How will you receive an event opportunity?',

      [

        'Tribe will send you an offer through the event’s group chat.',

        'Tribe will assign an event directly to your Reservations tab.',

        'Tribe will email you an offer to review and confirm.',

        'Tribe will add an available event to your personal calendar.',

      ],

      2,

      'Tribe sends event offers by email. Review the details and confirm that you can commit before accepting the event.',

    ),

  ]),

  check('event-guide', 'Before an Event', 'Event Guide', [

    choice(

      'what-guide-is',

      'What is the event guide?',

      [

        'A plan explaining how to prepare for and lead your assigned event.',

     'A walkthrough explaining how to use the Tribe web app during an event.',

        'A handbook explaining the general expectations for every Tribe host.',

        'A catalog explaining the activities you can choose for your event.',

      ],

      0,

      'After you accept an event, Tribe sends its event guide. It walks you through that specific experience so you know what to prepare and what to do throughout the event.',

    ),

    choice(

      'guide-information',

      'What information does the event guide contain?',

      [

        'Guest profiles, shared interests, and suggested conversation topics.',

     'Web app navigation, account settings, and instructions for adding friends.',

        'Hosting policies, application requirements, and general payment terms.',

        'The event agenda, exact times, host instructions, and venue arrangements.',

      ],

      3,

      'The guide gives you the agenda, timings, host instructions, meeting point, and venue arrangements. For ticketed activities, it also explains how to handle tickets.',

    ),

  ]),

  check('arrival-portal', 'Before an Event', 'Arrival & the Event Portal', [

    choice(

      'portal-capabilities',

      'What does the event portal let you do?',

      [

        'Review event offers and confirm your hosting availability.',

        'Message the group and record which guests attend.',

        'Manage reservations and move guests off the waiting list.',

        'Review guest profiles and manage your friend requests.',

      ],

      1,

      'Use the group chat to coordinate with guests and Check In Guests to record attendance. These tools help you manage arrivals and keep the group informed.',

    ),

    task(

      'arrival-message',

      'arrival-message',

      TASK_COPY['arrival-message'][1],

      'An arrival message helps guests find you. Open Reservations → the event → Enter Portal, review the guidelines and meeting point, then send the group a clear update about where you’re waiting.',

    ),

    choice(

      'venue-staff',

      'Your event guide says to check in with venue staff when you arrive. What should you do?',

      [

        'Confirm the venue is open, then gather the guests outside.',

        'Introduce yourself as the Tribe host and confirm the arrangements.',

        'Gather all the guests first, then approach staff as a group.',

        'Begin welcoming guests, then check with staff when the activity starts.',

      ],

      1,

      'Check in with staff before welcoming the group. Confirm the reservation or arrangements in your guide so you can resolve any issues before the activity begins.',

    ),

    task(

      'guest-attendance',

      'guest-attendance',

      TASK_COPY['guest-attendance'][1],

      'Open Check In Guests and record attendance accurately. Check Alex in when Alex arrives. Mark Jordan as a no-show after the event ends without Jordan attending.',

    ),

    choice(

      'late-arrivals',

      'It’s time to start, but two guests are still on their way. What should you do?',

      [

        'Wait for both guests, then begin the event with everyone together.',

        'Check both guests in now so they can join the group when they arrive.',

        'Start on time, coordinate through chat, and check them in on arrival.',

        'Ask the group to wait while you go outside to look for the late guests.',

      ],

      2,

      'Start with the guests who are there. Keep an eye on the group chat to help late arrivals find you, and check them in once they actually arrive.',

    ),

  ]),

  check('conversation', 'Facilitation', 'Be a Source of Conversation', [

    choice(

      'bring-energy',

      'The group is quiet, and conversation keeps stopping. What should you do?',

      [

        'Give everyone more time to start conversations themselves.',

        'Ask a light question, share a story, and invite others into the conversation.',

        'Keep talking yourself so there are no awkward silences.',

        'Move straight to the next activity to keep everyone occupied.',

      ],

      1,

      'Your energy helps set the tone. Questions, stories, and humor can make you fun to be around and help guests feel comfortable joining in. Leave room for them to contribute, too.',

    ),

  ]),

  check('connections', 'Facilitation', 'Help Guests Connect', [

    choice(

      'make-introduction',

      'Two guests share an interest but haven’t spoken to each other. What is the best way to help them connect?',

      [

        'Bring them together and introduce them through their shared interest.',

        'Tell each guest about the other and suggest they talk later.',

        'Keep talking to each guest separately about the shared interest.',

        'Mention the shared interest to the group and let them find each other.',

      ],

      0,

      'Make the connection yourself. A personal introduction gives guests an easy starting point and helps them move into a conversation together.',

    ),

  ]),

  check('safety', 'Facilitation', 'Safety Comes First', [

    choice(

      'safety-first',

      'A guest tells you another attendee is harassing them. What is your first priority?',

      [

        'Hear both sides together before taking steps to support the guest.',

        'Ask the guest to avoid the attendee while you continue the activity.',

        'Support the guest and take safe steps to address the situation.',

        'Send Tribe a report and wait for a response before taking action.',

      ],

      2,

      'Prioritize the safety of the person affected. Support them, involve venue staff or security when needed, and report the incident to Tribe.',

    ),

  ]),

  check('closing-event', 'Facilitation', 'Closing the Event', [

    task(

      'send-friend-request',

      'send-request',

      TASK_COPY['send-request'][1],

      'Open Chats → Matches → People You Met, find Alex, and tap Send Request. Friend requests let guests continue connections they made at the event.',

    ),

    task(

      'accept-friend-request',

      'accept-request',

      TASK_COPY['accept-request'][1],

      'Open Chats → Matches → Friend Requests and accept Jordan’s request. Jordan will then appear in your friends list.',

    ),

    task(

      'contact-message',

      'contact-message',

      TASK_COPY['contact-message'][1],

      'Open Chats → Friends, find Alex, tap Chat, and send your message. A follow-up gives you a way to continue the conversation after the event.',

    ),

  ]),

  check('expectations', 'Operations', 'Host Expectations', [

    choice(

      'host-removal',

      'Which action may put your hosting status at risk?',

      [

        'Declining an event offer before you commit to hosting it.',

        'Asking Tribe to clarify the arrangements in your event guide.',

        'Adjusting the event flow to handle an unexpected delay.',

        'Dropping out of a confirmed event without a real emergency.',

      ],

      3,

      'Once you commit, guests and Tribe rely on you to host. Dropping out without a real emergency—especially within 48 hours—or repeatedly canceling can lead to removal as a host.',

    ),

  ]),

];

export function sectionCheckFor(lessonId) {return SECTION_CHECKS.find(check=>check.afterLesson===lessonId)??null;}

export function initialTaskState(kind) {

  return {screen:kind==='guest-attendance'?'portal':'home',contact:'alex',meetingFrom:'guidelines',tab:'matches',accordion:null,

    attendance:{alex:'pending',jordan:'pending'},requestSent:false,accepted:false,requestDeclined:false,text:'',messages:[],actions:[]};

}

const SCREENS=new Set(['home','reservations','event','guidelines','meeting','portal','attendance','chats','contact-chat','agenda']);

export function sanitizeTaskState(kind,state) {

  const clean=initialTaskState(kind);

  if(!state||typeof state!=='object'||Array.isArray(state))return clean;

  if(SCREENS.has(state.screen))clean.screen=state.screen;

  if(['alex','jordan'].includes(state.contact))clean.contact=state.contact;

  if(['event','portal','guidelines'].includes(state.meetingFrom))clean.meetingFrom=state.meetingFrom;

  if(['matches','chats','friends'].includes(state.tab))clean.tab=state.tab;

  if(['people','requests'].includes(state.accordion))clean.accordion=state.accordion;

  for(const id of ['alex','jordan'])if(['pending','checked-in','no-show'].includes(state.attendance?.[id]))clean.attendance[id]=state.attendance[id];

  clean.requestSent=state.requestSent===true;clean.accepted=state.accepted===true;clean.requestDeclined=state.requestDeclined===true;

  clean.text=typeof state.text==='string'?state.text.slice(0,1000):'';

  clean.messages=Array.isArray(state.messages)?state.messages.filter(message=>typeof message==='string'&&message.trim()).slice(-20).map(message=>message.slice(0,1000)):[];

  clean.actions=Array.isArray(state.actions)?state.actions.filter(action=>typeof action==='string').slice(-200):[];

  return clean;

}

const hasSequence=(actions,required)=>{let index=0;for(const action of actions)if(action===required[index])index++;return index===required.length;};

export function isPracticeCorrect(question,answer) {

  if(question.type==='choice')return answer===question.correct;

  if(question.type==='order')return Array.isArray(answer)&&answer.length===question.items.length&&question.items.every((item,index)=>answer[index]===item.id);

  const state=sanitizeTaskState(question.taskKind,answer);

  switch(question.taskKind) {

    case 'arrival-message':return state.messages.length>0&&hasSequence(state.actions,['event','enter-portal','guidelines-reviewed','meeting-reviewed','send-group-message']);

    case 'guest-attendance':return state.attendance.alex==='checked-in'&&state.attendance.jordan==='no-show'&&state.actions.includes('check-in-guests');

    case 'send-request':return state.requestSent&&hasSequence(state.actions,['chats','people','send-request:alex']);

    case 'accept-request':return state.accepted&&hasSequence(state.actions,['chats','requests','accept-request:jordan']);

    case 'contact-message':return state.messages.length>0&&hasSequence(state.actions,['chats','friends','chat:alex','send-contact-message:alex']);

    default:return false;

  }

}

export function movePracticeItem(order,from,to) {

  if(!Number.isInteger(from)||!Number.isInteger(to)||from<0||to<0||from>=order.length||to>=order.length||from===to)return order;

  const next=[...order],[item]=next.splice(from,1);next.splice(to,0,item);return next;

}

export function sanitizePracticeDraft(check,draft) {

  const answers={},passed=[];

  for(const question of check.questions) {

    const answer=draft?.answers?.[question.id];

    if(question.type==='order') {

      const ids=new Set(question.items.map(item=>item.id));

      answers[question.id]=Array.isArray(answer)&&answer.length===ids.size&&new Set(answer).size===ids.size&&answer.every(id=>ids.has(id))?[...answer]:[...question.initialOrder];

    }else if(question.type==='task')answers[question.id]=sanitizeTaskState(question.taskKind,answer);

    else if(question.choices.some(choice=>choice.id===answer))answers[question.id]=answer;

    if(draft?.passed?.includes(question.id)&&isPracticeCorrect(question,answers[question.id]))passed.push(question.id);

  }

  const index=Number.isInteger(draft?.index)?Math.max(0,Math.min(check.questions.length-1,draft.index)):0;

  return {answers,passed,index:passed.length===check.questions.length?index:Math.min(index,check.questions.findIndex(question=>!passed.includes(question.id)))};

}
